import { Button } from "antd";
import { useEffect, useRef, useState } from "react";
import "./index.less";
import { getCameraConfig } from "../../../api/device/sensor";

interface IProps {
    base64String: string,
    canvasWidth: number,
    canvasHeight: number,
    sensorParamId?: number, // 传入 sensorParamId 则表示编辑模式，会加载已有数据
    onCancel?: () => void
    onConfirm?: (coords: { x: number, y: number }[][][]) => void
}

// 组合数据结构
interface PolygonGroup {
    id: number;
    name: string;
    polygonIndices: number[]; // 包含的多边形索引
    color: string;
}

export function Base64ImageCanvas(props: IProps) {
    const imageCanvasRef = useRef(null);
    const drawCanvasRef = useRef(null);

    const imgSizeRef = useRef({ width: 0, height: 0 });

    const [imgWidth, setImgWidth] = useState(0);
    const [imgHeight, setImgHeight] = useState(0);
    const [loading, setLoading] = useState(false);

    // 多边形相关状态
    const [polygons, setPolygons] = useState<{ x: number, y: number }[][]>([]);
    const [currentPolygon, setCurrentPolygon] = useState<{ x: number, y: number }[]>([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
    const [isComplete, setIsComplete] = useState(false);
    const [activePolygonIndex, setActivePolygonIndex] = useState<number>(-1);

    // 组合相关状态
    const [groups, setGroups] = useState<PolygonGroup[]>([]);
    const [selectedPolygons, setSelectedPolygons] = useState<number[]>([]); // 选中的多边形索引
    const [isGroupMode, setIsGroupMode] = useState(false); // 是否处于选择模式
    const [nextGroupId, setNextGroupId] = useState(1);

    const fetchPolygonsData = async () => {
    if (!props.sensorParamId) return;

    setLoading(true);
    try {
        console.log("开始获取相机配置，sensorParamId:", props.sensorParamId);
        const response = await getCameraConfig(props.sensorParamId);
        console.log("获取相机配置响应:", response);
        
        const responseData = response as any;
        if (response && responseData.polygons) {
            console.log("开始初始化多边形数据");
            initializePolygonsFromData(responseData.polygons);
        } else {
            console.warn("响应数据格式不正确:", response);
        }
    } catch (error) {
        console.error('获取多边形数据失败:', error);
        // 更详细的错误信息
        if (error instanceof Error) {
            console.error('错误名称:', error.name);
            console.error('错误信息:', error.message);
            console.error('错误堆栈:', error.stack);
        } else {
            console.error('未知错误类型:', typeof error, error);
        }
    } finally {
        setLoading(false);
    }
};

    // 绘制图片到底层 canvas
    useEffect(() => {
        const imageCanvas = imageCanvasRef.current;
        const imgCtx = imageCanvas.getContext('2d');
        const img = new Image();

        img.src = 'data:image/jpeg;base64,' + props.base64String;

        img.onload = () => {
            imageCanvas.width = props.canvasWidth;
            imageCanvas.height = props.canvasHeight;
            setImgHeight(img.height);
            setImgWidth(img.width);
            imgSizeRef.current = { width: img.width, height: img.height };
            imgCtx.drawImage(img, 0, 0, props.canvasWidth, props.canvasHeight);

            // 重置绘制状态，但如果存在初始数据则不清除
            const drawCanvas = drawCanvasRef.current;
            const drawCtx = drawCanvas.getContext('2d');
            drawCtx.clearRect(0, 0, props.canvasWidth, props.canvasHeight);

            // 只有在没有初始数据时才重置所有状态
            if (props.sensorParamId) {
                // 编辑模式：从数据库获取数据
                fetchPolygonsData();
            } else {
                // 创建模式：重置所有状态
                console.log("创建模式，重置所有状态");
                setPolygons([]);
                setCurrentPolygon([]);
                setIsComplete(false);
                setActivePolygonIndex(-1);
                setSelectedPolygons([]);
                setGroups([]);
                setNextGroupId(1);
            }
        };
    }, [props.base64String, props.canvasWidth, props.canvasHeight]);

    const initializePolygonsFromData = (polygonData: { x: number, y: number }[][][]) => {
        console.log("初始化多边形数据:", polygonData);

        const allPolygons: { x: number, y: number }[][] = [];
        const newGroups: PolygonGroup[] = [];
        let polygonIndexCounter = 0;
        let groupIdCounter = 1;

        const groupColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];

        polygonData.forEach((group, groupIndex) => {
            if (group.length === 1) {
                // 单独的多边形
                const canvasPolygon = convertImageCoordsToCanvas(group[0]);
                allPolygons.push(canvasPolygon);
                console.log(`添加单独多边形 ${polygonIndexCounter}:`, canvasPolygon);
                polygonIndexCounter++;
            } else {
                // 组合的多边形
                const groupPolygonIndices: number[] = [];
                group.forEach(polygon => {
                    const canvasPolygon = convertImageCoordsToCanvas(polygon);
                    allPolygons.push(canvasPolygon);
                    groupPolygonIndices.push(polygonIndexCounter);
                    console.log(`添加组合多边形 ${polygonIndexCounter}:`, canvasPolygon);
                    polygonIndexCounter++;
                });

                newGroups.push({
                    id: groupIdCounter,
                    name: `组合 ${groupIdCounter}`,
                    polygonIndices: groupPolygonIndices,
                    color: groupColors[(groupIdCounter - 1) % groupColors.length]
                });
                groupIdCounter++;
            }
        });

        setPolygons(allPolygons);
        setGroups(newGroups);
        setNextGroupId(groupIdCounter);
        setActivePolygonIndex(allPolygons.length > 0 ? 0 : -1);
    };

    // 获取多边形的颜色
    const getPolygonColor = (polygonIndex: number) => {
        // 首先检查是否在选中状态
        if (selectedPolygons.includes(polygonIndex)) {
            return {
                stroke: '#ff00ff', // 洋红色表示选中
                fill: 'rgba(255, 0, 255, 0.3)'
            };
        }

        // 检查是否属于某个组合
        const group = groups.find(g => g.polygonIndices.includes(polygonIndex));
        if (group) {
            return {
                stroke: group.color,
                fill: `${group.color}33` // 添加透明度
            };
        }

        // 默认颜色
        const isActive = polygonIndex === activePolygonIndex;
        return {
            stroke: isActive ? 'blue' : 'red',
            fill: isActive ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)'
        };
    };

    // 绘制多边形和预览线
    useEffect(() => {
        const drawCanvas = drawCanvasRef.current;
        const drawCtx = drawCanvas.getContext('2d');

        // 清除画布
        drawCtx.clearRect(0, 0, props.canvasWidth, props.canvasHeight);

        // 绘制所有已完成的多边形
        polygons.forEach((polygon, polygonIndex) => {
            if (polygon.length === 0) return;

            const colors = getPolygonColor(polygonIndex);

            drawCtx.strokeStyle = colors.stroke;
            drawCtx.lineWidth = selectedPolygons.includes(polygonIndex) ? 4 : 2;
            drawCtx.fillStyle = colors.fill;

            // 开始绘制多边形
            drawCtx.beginPath();
            drawCtx.moveTo(polygon[0].x, polygon[0].y);

            // 绘制边
            for (let i = 1; i < polygon.length; i++) {
                drawCtx.lineTo(polygon[i].x, polygon[i].y);
            }
            drawCtx.closePath();
            drawCtx.fill();
            drawCtx.stroke();

            // 绘制顶点
            polygon.forEach((point, pointIndex) => {
                drawCtx.beginPath();
                drawCtx.arc(point.x, point.y, 4, 0, Math.PI * 2);
                drawCtx.fillStyle = colors.stroke;
                drawCtx.fill();
                drawCtx.strokeStyle = 'white';
                drawCtx.lineWidth = 1;
                drawCtx.stroke();

                // 绘制顶点编号
                drawCtx.fillStyle = 'white';
                drawCtx.font = '12px Arial';
                drawCtx.textAlign = 'center';
                drawCtx.textBaseline = 'middle';
                drawCtx.fillText(`${polygonIndex + 1}-${pointIndex + 1}`, point.x, point.y);
            });

            // 如果多边形属于组合，显示组合标识
            const group = groups.find(g => g.polygonIndices.includes(polygonIndex));
            if (group) {
                const center = getPolygonCenter(polygon);
                drawCtx.fillStyle = group.color;
                drawCtx.font = 'bold 14px Arial';
                drawCtx.textAlign = 'center';
                drawCtx.textBaseline = 'middle';
                drawCtx.fillText(`G${groups.indexOf(group) + 1}`, center.x, center.y - 20);
            }
        });

        // 绘制当前正在绘制的多边形
        if (currentPolygon.length > 0) {
            drawCtx.strokeStyle = 'green';
            drawCtx.lineWidth = 2;
            drawCtx.fillStyle = 'rgba(0, 255, 0, 0.2)';

            // 开始绘制当前多边形
            drawCtx.beginPath();
            drawCtx.moveTo(currentPolygon[0].x, currentPolygon[0].y);

            // 绘制已确定的边
            for (let i = 1; i < currentPolygon.length; i++) {
                drawCtx.lineTo(currentPolygon[i].x, currentPolygon[i].y);
            }

            // 如果多边形已完成，闭合并填充
            if (isComplete) {
                drawCtx.closePath();
                drawCtx.fill();
                drawCtx.stroke();
            } else {
                // 绘制当前边（预览线）
                drawCtx.lineTo(currentPos.x, currentPos.y);
                drawCtx.stroke();
            }

            // 绘制当前多边形的顶点
            currentPolygon.forEach((point, index) => {
                drawCtx.beginPath();
                drawCtx.arc(point.x, point.y, 4, 0, Math.PI * 2);
                drawCtx.fillStyle = 'green';
                drawCtx.fill();
                drawCtx.strokeStyle = 'white';
                drawCtx.lineWidth = 1;
                drawCtx.stroke();

                // 绘制顶点编号
                drawCtx.fillStyle = 'white';
                drawCtx.font = '12px Arial';
                drawCtx.textAlign = 'center';
                drawCtx.textBaseline = 'middle';
                drawCtx.fillText((index + 1).toString(), point.x, point.y);
            });
        }

    }, [polygons, currentPolygon, currentPos, isComplete, activePolygonIndex, selectedPolygons, groups, props.canvasWidth, props.canvasHeight]);

    // 计算多边形中心点
    const getPolygonCenter = (polygon: { x: number, y: number }[]) => {
        const sum = polygon.reduce((acc, point) => ({
            x: acc.x + point.x,
            y: acc.y + point.y
        }), { x: 0, y: 0 });

        return {
            x: sum.x / polygon.length,
            y: sum.y / polygon.length
        };
    };

    // 处理鼠标移动事件
    const handleMouseMove = (e) => {
        const drawCanvas = drawCanvasRef.current;
        const rect = drawCanvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setCurrentPos({ x, y });
    };

    // 处理鼠标点击事件
    const handleMouseDown = (e) => {
        // 如果处于选择模式，处理多边形选择
        if (isGroupMode) {
            handlePolygonSelection(e);
            return;
        }

        // 正常的绘制逻辑
        if (isComplete && currentPolygon.length > 0) {
            setPolygons(prev => [...prev, currentPolygon]);
            setCurrentPolygon([]);
            setIsComplete(false);
            setActivePolygonIndex(polygons.length);
            return;
        }

        const drawCanvas = drawCanvasRef.current;
        const rect = drawCanvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (e.detail === 2) {
            if (currentPolygon.length >= 3) {
                setIsComplete(true);
            }
            return;
        }

        setCurrentPolygon(prev => [...prev, { x, y }]);
        setIsDrawing(true);
    };

    // 处理多边形选择
    const handlePolygonSelection = (e) => {
        const drawCanvas = drawCanvasRef.current;
        const rect = drawCanvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 检查点击是否在某个多边形内
        let clickedPolygonIndex = -1;

        polygons.forEach((polygon, index) => {
            if (isPointInPolygon({ x, y }, polygon)) {
                clickedPolygonIndex = index;
            }
        });

        if (clickedPolygonIndex !== -1) {
            setSelectedPolygons(prev => {
                if (prev.includes(clickedPolygonIndex)) {
                    // 如果已经选中，则取消选中
                    return prev.filter(i => i !== clickedPolygonIndex);
                } else {
                    // 如果未选中，则添加选中
                    return [...prev, clickedPolygonIndex];
                }
            });
        }
    };

    // 判断点是否在多边形内
    const isPointInPolygon = (point: { x: number, y: number }, polygon: { x: number, y: number }[]) => {
        const x = point.x, y = point.y;
        let inside = false;

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i].x, yi = polygon[i].y;
            const xj = polygon[j].x, yj = polygon[j].y;

            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

            if (intersect) inside = !inside;
        }

        return inside;
    };

    // 创建组合
    const handleCreateGroup = () => {
        if (selectedPolygons.length < 2) {
            alert('请至少选择2个多边形来创建组合！');
            return;
        }

        const groupColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
        const newGroup: PolygonGroup = {
            id: nextGroupId,
            name: `组合 ${nextGroupId}`,
            polygonIndices: [...selectedPolygons],
            color: groupColors[(nextGroupId - 1) % groupColors.length]
        };

        setGroups(prev => [...prev, newGroup]);
        setNextGroupId(prev => prev + 1);
        setSelectedPolygons([]);
        setIsGroupMode(false);
    };

    // 解散组合
    const handleUngroup = (groupId: number) => {
        setGroups(prev => prev.filter(group => group.id !== groupId));
    };

    // 选择所有组合中的多边形
    const handleSelectGroup = (group: PolygonGroup) => {
        setSelectedPolygons(group.polygonIndices);
    };

    // 开始新的多边形
    const handleNewPolygon = () => {
        if (currentPolygon.length > 0) {
            if (currentPolygon.length >= 3) {
                setPolygons(prev => [...prev, currentPolygon]);
                setCurrentPolygon([]);
                setIsComplete(false);
                setActivePolygonIndex(polygons.length);
            } else {
                alert('当前多边形至少需要3个顶点才能完成！');
            }
        } else {
            setCurrentPolygon([]);
            setIsComplete(false);
            setActivePolygonIndex(-1);
        }
    };

    // 撤销当前多边形的最后一个点
    const handleUndo = () => {
        if (currentPolygon.length > 0) {
            setCurrentPolygon(prev => prev.slice(0, -1));
            setIsComplete(false);
        }
    };

    // 重置当前多边形
    const handleReset = () => {
        setCurrentPolygon([]);
        setIsComplete(false);
        setIsDrawing(false);
    };

    // 重置所有多边形
    const handleResetAll = () => {
        setPolygons([]);
        setCurrentPolygon([]);
        setIsComplete(false);
        setIsDrawing(false);
        setActivePolygonIndex(-1);
        setSelectedPolygons([]);
        setGroups([]);
        setNextGroupId(1);

        const drawCanvas = drawCanvasRef.current;
        const drawCtx = drawCanvas.getContext('2d');
        drawCtx.clearRect(0, 0, props.canvasWidth, props.canvasHeight);
    };

    // 完成当前多边形
    const handleComplete = () => {
        if (currentPolygon.length >= 3) {
            setIsComplete(true);
        } else {
            alert('至少需要3个点才能形成多边形！');
        }
    };

    // 删除指定的多边形
    const handleDeletePolygon = (index: number) => {
        setPolygons(prev => prev.filter((_, i) => i !== index));

        // 更新选中状态
        setSelectedPolygons(prev => prev.filter(i => i !== index));

        // 更新组合
        setGroups(prev =>
            prev.map(group => ({
                ...group,
                polygonIndices: group.polygonIndices
                    .filter(i => i !== index)
                    .map(i => i > index ? i - 1 : i) // 调整索引
            })).filter(group => group.polygonIndices.length > 0) // 移除空组合
        );

        if (activePolygonIndex === index) {
            setActivePolygonIndex(-1);
        } else if (activePolygonIndex > index) {
            setActivePolygonIndex(activePolygonIndex - 1);
        }
    };

    // 将canvas坐标转换为原始图片坐标
    // 将canvas坐标转换为原始图片坐标
    const convertToImageCoords = () => {
        const allPolygons = [...polygons];
        if (currentPolygon.length >= 3 && isComplete) {
            allPolygons.push(currentPolygon);
        }

        const { width: imgW, height: imgH } = imgSizeRef.current;

        // 转换坐标
        const convertedPolygons = allPolygons.map(polygon =>
            polygon.map(point => ({
                x: Math.floor(point.x / props.canvasWidth * imgW),
                y: Math.floor(point.y / props.canvasHeight * imgH)
            }))
        );

        // 构建新的数据结构：组合中的多边形放在额外数组中，单独多边形直接放置
        const result: { x: number, y: number }[][][] = [];

        // 首先处理组合
        groups.forEach(group => {
            const groupPolygons: { x: number, y: number }[][] = [];
            group.polygonIndices.forEach(index => {
                if (index < convertedPolygons.length) {
                    groupPolygons.push(convertedPolygons[index]);
                }
            });
            if (groupPolygons.length > 0) {
                result.push(groupPolygons);
            }
        });

        // 然后处理未分组的多边形
        const groupedIndices = new Set(groups.flatMap(group => group.polygonIndices));
        convertedPolygons.forEach((polygon, index) => {
            if (!groupedIndices.has(index)) {
                result.push([polygon]); // 单独多边形也放在数组中，但只有一项
            }
        });

        return result;
    };

    // 将图片坐标转换为canvas坐标
    const convertImageCoordsToCanvas = (polygon: { x: number, y: number }[]) => {
        const { width: imgW, height: imgH } = imgSizeRef.current;
        
        console.log("坐标转换 - 图片尺寸:", imgW, imgH, "画布尺寸:", props.canvasWidth, props.canvasHeight);
        console.log("原始坐标:", polygon);
        
        // 添加安全检查
        if (imgW === 0 || imgH === 0) {
            console.error("图片尺寸为0，无法进行坐标转换");
            return polygon; // 或者返回空数组，或者抛出错误
        }
        
        const converted = polygon.map(point => ({
            x: (point.x / imgW) * props.canvasWidth,
            y: (point.y / imgH) * props.canvasHeight
        }));
        
        console.log("转换后坐标:", converted);
        return converted;
    };

    return (
        <>
            <div style={{ position: 'relative', width: props.canvasWidth, height: props.canvasHeight + 80 }}>
                {/* 底层 canvas，用于显示图片 */}
                <canvas
                    ref={imageCanvasRef}
                    width={props.canvasWidth}
                    height={props.canvasHeight}
                    style={{ position: 'absolute', top: 25, left: 0, zIndex: 0 }}
                ></canvas>

                {/* 上层 canvas，用于绘制多边形 */}
                <canvas
                    ref={drawCanvasRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    width={props.canvasWidth}
                    height={props.canvasHeight}
                    style={{
                        position: 'absolute',
                        top: 25,
                        left: 0,
                        zIndex: 1,
                        cursor: isGroupMode ? 'pointer' : 'crosshair'
                    }}
                ></canvas>

                {/* 操作说明和坐标显示 */}
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    <p>
                        操作说明：
                        {isGroupMode ?
                            "选择模式：点击多边形进行选择，选中后点击'创建组合'" :
                            "绘制模式：点击添加顶点，双击完成绘制，至少需要3个顶点"
                        }
                    </p>
                </div>

                {/* 显示多边形列表 */}
                <div style={{ marginTop: '10px', maxHeight: '100px', overflowY: 'auto' }}>
                    <p>多边形列表：</p>
                    {polygons.map((polygon, polygonIndex) => (
                        <div key={polygonIndex} style={{
                            fontSize: '12px',
                            padding: '2px 5px',
                            backgroundColor: activePolygonIndex === polygonIndex ? '#e6f7ff' :
                                selectedPolygons.includes(polygonIndex) ? '#f0f8ff' : 'transparent',
                            border: activePolygonIndex === polygonIndex ? '1px solid #1890ff' :
                                selectedPolygons.includes(polygonIndex) ? '1px solid #ff00ff' : '1px solid transparent'
                        }}>
                            多边形{polygonIndex + 1} - {polygon.length}个顶点
                        </div>
                    ))}
                    {currentPolygon.length > 0 && (
                        <div style={{ fontSize: '12px', padding: '2px 5px', color: 'green' }}>
                            当前多边形 - {currentPolygon.length}个顶点 {isComplete && '(已完成)'}
                        </div>
                    )}
                </div>

                {/* 组合列表 */}
                {groups.length > 0 && (
                    <div style={{ marginTop: '10px', maxHeight: '100px', overflowY: 'auto' }}>
                        <p>组合列表：</p>
                        {groups.map((group, index) => (
                            <div key={group.id} style={{
                                fontSize: '12px',
                                padding: '2px 5px',
                                borderLeft: `4px solid ${group.color}`,
                                backgroundColor: '#f9f9f9',
                                marginBottom: '2px'
                            }}>
                                {group.name} - {group.polygonIndices.length}个多边形
                                {/* <Button
                                    size="small"
                                    type="link"
                                    onClick={() => handleSelectGroup(group)}
                                >
                                    选择
                                </Button>
                                <Button
                                    size="small"
                                    type="link"
                                    danger
                                    onClick={() => handleUngroup(group.id)}
                                >
                                    解散
                                </Button> */}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 控制按钮 */}
            <div className="canvas-operation-footer">
                {/* 核心操作按钮 */}
                <Button
                    className="canvas-btn canvas-btn--primary"
                    type="primary"
                    size="middle"
                    onClick={() => {
                        const allPolygons = convertToImageCoords();
                        if (allPolygons.length > 0) {
                            props.onConfirm?.(allPolygons);
                        } else {
                            alert('请至少绘制一个完整的多边形！');
                        }
                    }}
                >
                    确认所有区域
                </Button>

                {/* 组合操作按钮 */}
                <Button
                    className="canvas-btn canvas-btn--dashed"
                    type={isGroupMode ? "primary" : "dashed"}
                    size="middle"
                    onClick={() => {
                        setIsGroupMode(!isGroupMode);
                        setSelectedPolygons([]);
                    }}
                >
                    {isGroupMode ? '退出选择模式' : '选择模式'}
                </Button>

                {isGroupMode && (
                    <Button
                        className="canvas-btn canvas-btn--primary"
                        type="primary"
                        size="middle"
                        onClick={handleCreateGroup}
                        disabled={selectedPolygons.length < 2}
                    >
                        创建组合 ({selectedPolygons.length})
                    </Button>
                )}

                {/* 绘制操作按钮 */}
                {!isGroupMode && (
                    <>
                        <Button
                            className="canvas-btn canvas-btn--dashed"
                            type="dashed"
                            size="middle"
                            onClick={handleNewPolygon}
                        >
                            开始新多边形
                        </Button>

                        <Button
                            className="canvas-btn canvas-btn--complete"
                            size="middle"
                            onClick={handleComplete}
                            disabled={isComplete || currentPolygon.length < 3}
                        >
                            完成当前多边形
                        </Button>
                    </>
                )}

                {/* 重置/撤销类按钮 */}
                <Button
                    className="canvas-btn canvas-btn--default"
                    size="middle"
                    onClick={handleUndo}
                    disabled={currentPolygon.length === 0}
                >
                    撤销上一个点
                </Button>

                <Button
                    className="canvas-btn canvas-btn--default"
                    size="middle"
                    onClick={handleReset}
                >
                    重置当前多边形
                </Button>

                {/* 风险操作按钮 */}
                <Button
                    className="canvas-btn canvas-btn--danger"
                    size="middle"
                    danger
                    onClick={handleResetAll}
                >
                    重置所有
                </Button>

                {/* 取消按钮 */}
                <Button
                    className="canvas-btn canvas-btn--cancel"
                    size="middle"
                    onClick={() => props.onCancel?.()}
                >
                    取消
                </Button>
            </div>
        </>
    );
};
